// TelcoTrack — object attribute filter: HP + Dig. count popovers (single expression field).

import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import {
  emptyCountFilter,
  emptyObjectAttributeFilterState,
  formatCountFilterBadge,
  isCountFilterActive,
  normalizeCountFilter,
  parseCountFilterExpression,
  simplifyObjectAttributeFilterState,
  type CountFilterValue,
  type ObjectAttributeFilterState,
} from '@core/shared-bridge';
import {
  claimFilterOverlay,
  releaseFilterOverlay,
} from '../../../utils/filter-overlay-coordinator.util';
import { filterOverlayPanelStyle } from '../../../utils/filter-overlay-position.util';

type CountDim = 'hasHp' | 'digitalniCesta';

@Component({
  selector: 'app-object-attribute-filter',
  templateUrl: './object-attribute-filter.component.html',
  styleUrls: ['./object-attribute-filter.component.scss'],
})
export class ObjectAttributeFilterComponent implements OnDestroy {
  /** Kept for API compatibility with map / objects list bindings. */
  @Input() objectTypes: string[] = [];
  @Input() value: ObjectAttributeFilterState = emptyObjectAttributeFilterState();
  /** Unused in simplified UI — kept so existing callers need no template churn. */
  @Input() statusOptions: { value: string; label: string }[] = [];
  @Input() disabled = false;
  /** Map shows this while lazy HP/Dig enrichment is in flight. */
  @Input() loading = false;

  @Output() valueChange = new EventEmitter<ObjectAttributeFilterState>();
  /** Fired when HP or Dig. panel opens — map uses this to start shared enrichment fetch. */
  @Output() panelOpen = new EventEmitter<CountDim>();

  @ViewChild('hpTrigger') private hpTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('dcTrigger') private dcTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('panelEl') private panelEl?: ElementRef<HTMLElement>;

  openDim: CountDim | null = null;
  /** Draft expression, e.g. `>3`, `<10`, `=0`. */
  draftExpr = '';
  panelStyle: { [key: string]: string } = {};

  private panelInBody = false;
  private panelOriginalParent: Node | null = null;
  private destroyed = false;
  private readonly reposition = (): void => this.updatePanelPosition();

  constructor(private readonly cdr: ChangeDetectorRef) {}

  get hp(): CountFilterValue {
    return normalizeCountFilter(this.value?.hasHp);
  }

  get digCesta(): CountFilterValue {
    return normalizeCountFilter(this.value?.digitalniCesta);
  }

  get openLabel(): string {
    return this.openDim === 'hasHp' ? 'HP' : 'Dig.';
  }

  hpBadge(): string {
    return formatCountFilterBadge(this.hp);
  }

  digBadge(): string {
    return formatCountFilterBadge(this.digCesta);
  }

  isActive(filter: CountFilterValue): boolean {
    return isCountFilterActive(filter);
  }

  dimTitle(dim: CountDim): string {
    const label = dim === 'hasHp' ? 'HP' : 'Dig.';
    const filter = dim === 'hasHp' ? this.hp : this.digCesta;
    if (this.loading) return `${label}: načítám data filtru…`;
    if (!isCountFilterActive(filter)) {
      return `${label}: filtr vypnutý (klik → např. >3)`;
    }
    return `${label}: ${formatCountFilterBadge(filter)}`;
  }

  dimAria(dim: CountDim): string {
    const label = dim === 'hasHp' ? 'HP' : 'Dig.';
    const filter = dim === 'hasHp' ? this.hp : this.digCesta;
    if (this.loading) return `Filtr ${label}: načítám`;
    if (!isCountFilterActive(filter)) return `Filtr ${label}: vypnutý`;
    return `Filtr ${label}: ${formatCountFilterBadge(filter)}`;
  }

  togglePanel(dim: CountDim, event: MouseEvent): void {
    event.stopPropagation();
    if (this.disabled) return;
    if (this.openDim === dim) {
      this.closePanel();
      return;
    }
    // Closes Objekty / Návaznosti / other toolbar dropdowns first.
    claimFilterOverlay(this);
    if (this.openDim) this.unmountPanel();
    this.openDim = dim;
    const current = dim === 'hasHp' ? this.hp : this.digCesta;
    this.draftExpr = formatCountFilterBadge(current);
    this.panelOpen.emit(dim);
    // Map host is OnPush — render the panel before measuring it.
    this.cdr.detectChanges();
    this.mountPanel();
    this.updatePanelPosition();
    this.focusExprInput();
  }

  /** Public so the shared filter-overlay coordinator can close us. */
  closePanel(): void {
    if (!this.openDim && !this.panelInBody) return;
    this.unmountPanel();
    this.openDim = null;
    this.panelStyle = {};
    releaseFilterOverlay(this);
    if (!this.destroyed) this.cdr.detectChanges();
  }

  applyDraft(): void {
    if (!this.openDim || this.loading) return;
    const parsed = parseCountFilterExpression(this.draftExpr);
    if (!isCountFilterActive(parsed)) {
      this.emitCount(this.openDim, emptyCountFilter());
      this.closePanel();
      return;
    }
    this.emitCount(this.openDim, parsed);
    this.closePanel();
  }

  clearDraft(event?: Event): void {
    event?.stopPropagation();
    if (!this.openDim) return;
    this.emitCount(this.openDim, emptyCountFilter());
    this.closePanel();
  }

  onDraftExprInput(raw: string): void {
    this.draftExpr = raw;
  }

  onDraftKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.applyDraft();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closePanel();
    }
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentPointerDown(event: MouseEvent): void {
    if (!this.openDim) return;
    const target = event.target as Node | null;
    if (!target) return;
    const panel = this.panelEl?.nativeElement;
    const trigger =
      this.openDim === 'hasHp'
        ? this.hpTrigger?.nativeElement
        : this.dcTrigger?.nativeElement;
    if (panel?.contains(target) || trigger?.contains(target)) return;
    this.closePanel();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.openDim) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closePanel();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.unmountPanel();
    this.openDim = null;
    releaseFilterOverlay(this);
  }

  private emitCount(key: CountDim, next: CountFilterValue): void {
    const base = simplifyObjectAttributeFilterState(this.value);
    this.valueChange.emit({ ...base, [key]: next });
  }

  private focusExprInput(): void {
    const input = this.panelEl?.nativeElement.querySelector<HTMLInputElement>(
      'input.tt-object-attr-filter__expr',
    );
    input?.focus();
    input?.select();
  }

  private mountPanel(): void {
    const panel = this.panelEl?.nativeElement;
    if (!panel || this.panelInBody) return;
    this.panelOriginalParent = panel.parentNode;
    document.body.appendChild(panel);
    this.panelInBody = true;
    window.addEventListener('resize', this.reposition);
    window.addEventListener('scroll', this.reposition, true);
  }

  private unmountPanel(): void {
    const panel = this.panelEl?.nativeElement;
    if (panel && this.panelInBody && this.panelOriginalParent) {
      this.panelOriginalParent.appendChild(panel);
    }
    this.panelInBody = false;
    this.panelOriginalParent = null;
    window.removeEventListener('resize', this.reposition);
    window.removeEventListener('scroll', this.reposition, true);
  }

  private updatePanelPosition(): void {
    if (!this.openDim) return;
    const trigger =
      this.openDim === 'hasHp'
        ? this.hpTrigger?.nativeElement
        : this.dcTrigger?.nativeElement;
    if (!trigger) return;
    const style = filterOverlayPanelStyle(
      trigger,
      this.panelEl?.nativeElement,
      200,
      { estimatedHeightPx: 120, preferBelow: true },
    );
    this.panelStyle = style;
    const panel = this.panelEl?.nativeElement;
    if (!panel) return;
    // Write inline so OnPush / a missed CD cycle cannot leave the panel at its static
    // position (above the toolbar). Always under the icon.
    panel.style.position = 'fixed';
    panel.style.top = style['top'] ?? '';
    panel.style.left = style['left'] ?? '';
    panel.style.bottom = 'auto';
    panel.style.right = 'auto';
    panel.style.minWidth = style['min-width'] ?? '';
    panel.style.zIndex = '50030';
  }
}
